import { debounce, fetchConfig } from '@theme/utilities';

const CUIT_LENGTH = 11;
const DNI_MIN = 7;
const DNI_MAX = 8;

/**
 * Single cart-level source of truth, shared across every mounted view (cart page + drawer
 * clone) so a value entered on one surface reflects on the other. Seeded once from the
 * first view's server-rendered markup.
 */
const state = {
  metodo: '',
  documento: '',
  cuit: '',
  facturaWanted: false,
  cuitFromDoc: false,
  cuitUserEdited: false,
};
let seeded = false;

/** Every mounted `<cautiva-delivery-capture>`. Re-rendered on each state change. */
const views = new Set();

/** Monotonic counter for per-instance radio names + ARIA ids (drawer clones must not collide). */
let instanceId = 0;

/** Whether the delivery form is complete enough to allow checkout. */
let allowed = true;

/** @param {string} value @param {number} max */
const toDigits = (value, max) => value.replace(/\D/g, '').slice(0, max);
/** DNI (7–8) or CUIT/CUIL (11). @param {string} d */
const isDocValid = (d) => d.length === CUIT_LENGTH || (d.length >= DNI_MIN && d.length <= DNI_MAX);
/** @param {string} d */
const isCuitValid = (d) => d.length === CUIT_LENGTH;

const methodChosen = () => state.metodo === 'envio' || state.metodo === 'retiro';
const docSatisfied = () => state.metodo !== 'envio' || isDocValid(state.documento);
const facturaSatisfied = () => !state.facturaWanted || isCuitValid(state.cuit);
const computeAllowed = () => methodChosen() && docSatisfied() && facturaSatisfied();

/**
 * Reconciles derived state: a CUIT is only carried when Factura A is requested. Until the
 * buyer touches the CUIT field, an 11-digit shipping document auto-mirrors into it (and
 * re-mirrors as the document changes, clearing when the document stops being a valid CUIT)
 * so they don't retype it. The buyer's first keystroke in the CUIT field hands them control
 * (`cuitUserEdited`) and stops all auto-mirroring — including after they clear it — so the
 * recipient's shipping document can never silently re-populate a CUIT the buyer deliberately
 * emptied. Unchecking Factura A discards the field and re-arms the mirror.
 */
const normalize = () => {
  if (!state.facturaWanted) {
    state.cuit = '';
    state.cuitFromDoc = false;
    state.cuitUserEdited = false;
    return;
  }
  if (state.cuitUserEdited) return;
  if (isCuitValid(state.documento)) {
    state.cuit = state.documento;
    state.cuitFromDoc = true;
  } else if (state.cuitFromDoc) {
    state.cuit = '';
    state.cuitFromDoc = false;
  }
};

/**
 * Persists all three cart attributes together (empty string clears one). Debounced and
 * abortable like the stock cart-note; the server stays the source of truth on failure.
 */
let activeFetch = null;
const persist = debounce(async () => {
  activeFetch?.abort();
  const controller = new AbortController();
  activeFetch = controller;
  try {
    const config = fetchConfig('json', {
      body: JSON.stringify({
        attributes: {
          metodo_entrega: state.metodo,
          documento: state.documento,
          cuit: state.cuit,
        },
      }),
    });
    await fetch(Theme.routes.cart_update_url, { ...config, signal: controller.signal });
  } catch {
    // Aborted or network error — next input retries; server HTML restores on the next morph.
  } finally {
    activeFetch = null;
  }
}, 300);

/**
 * Applies the checkout gate to every cart CTA. Disabling is best-effort for the accelerated
 * (Shop Pay etc.) buttons — they can still bypass the cart entirely (R2 in the spec), so
 * fulfillment remains the backstop for the shipping document.
 */
const enforce = () => {
  allowed = computeAllowed();
  for (const btn of document.querySelectorAll('.cart__ctas [name="checkout"]')) {
    btn.setAttribute('aria-disabled', allowed ? 'false' : 'true');
  }
  for (const el of document.querySelectorAll('.cart__ctas .additional-checkout-buttons')) {
    el.classList.toggle('cautiva-delivery-blocked', !allowed);
  }
};

const render = () => {
  for (const view of views) view.render();
};

/**
 * @param {Partial<typeof state>} patch
 */
const setState = (patch) => {
  Object.assign(state, patch);
  normalize();
  render();
  enforce();
  persist();
};

/**
 * Mandatory delivery-method choice with a conditional destinatario-document field and an
 * optional Factura A CUIT field. Persists `metodo_entrega` / `documento` / `cuit` as cart
 * attributes so they reach the order before `orders/paid`. See the snippet doc header and
 * docs/cart-delivery-and-document-capture.md.
 */
class CautivaDeliveryCapture extends HTMLElement {
  /** @type {HTMLInputElement | null} */ #docInput = null;
  /** @type {HTMLInputElement | null} */ #cuitInput = null;
  /** @type {HTMLInputElement | null} */ #facturaToggle = null;
  /** @type {NodeListOf<HTMLInputElement> | null} */ #radios = null;
  /** @type {HTMLElement | null} */ #docError = null;
  /** @type {HTMLElement | null} */ #cuitError = null;
  /** @type {HTMLElement | null} */ #methodError = null;
  #controller = new AbortController();

  connectedCallback() {
    this.#docInput = this.querySelector('.cautiva-delivery__doc .cautiva-delivery__input');
    this.#cuitInput = this.querySelector('.cautiva-delivery__cuit .cautiva-delivery__input');
    this.#facturaToggle = this.querySelector('.cautiva-delivery__factura-toggle');
    this.#radios = this.querySelectorAll('.cautiva-delivery__radio');
    this.#docError = this.querySelector('.cautiva-delivery__doc .cautiva-delivery__error');
    this.#cuitError = this.querySelector('.cautiva-delivery__cuit .cautiva-delivery__error');
    this.#methodError = this.querySelector('.cautiva-delivery__method-error');

    if (!seeded) this.#seed();

    const uid = ++instanceId;
    // Per-instance radio name so native grouping stays within this view; ARIA ids wired here
    // (not in markup) so the drawer clone doesn't duplicate ids.
    for (const radio of this.#radios) radio.name = `cautiva-metodo-${uid}`;
    this.#wireDescribedby(this.#docInput, uid, 'doc');
    this.#wireDescribedby(this.#cuitInput, uid, 'cuit');

    const { signal } = this.#controller;
    for (const radio of this.#radios) {
      radio.addEventListener('change', () => setState({ metodo: radio.value }), { signal });
    }
    this.#docInput?.addEventListener('input', this.#onDocInput, { signal });
    this.#cuitInput?.addEventListener('input', this.#onCuitInput, { signal });
    this.#facturaToggle?.addEventListener(
      'change',
      () => setState({ facturaWanted: Boolean(this.#facturaToggle?.checked) }),
      { signal }
    );

    views.add(this);
    this.render();
    enforce();
  }

  disconnectedCallback() {
    views.delete(this);
    this.#controller.abort();
    this.#controller = new AbortController();
  }

  /**
   * Assigns unique ids to a field's help + error text and points the input's
   * `aria-describedby` at them.
   * @param {HTMLInputElement | null} input
   * @param {number} uid
   * @param {string} key
   */
  #wireDescribedby(input, uid, key) {
    const field = input?.closest('.cautiva-delivery__doc, .cautiva-delivery__cuit');
    if (!input || !field) return;
    const ids = [];
    const help = field.querySelector('.cautiva-delivery__help');
    const error = field.querySelector('.cautiva-delivery__error');
    if (help) {
      help.id = `cautiva-${key}-help-${uid}`;
      ids.push(help.id);
    }
    if (error) {
      error.id = `cautiva-${key}-error-${uid}`;
      ids.push(error.id);
    }
    if (ids.length) input.setAttribute('aria-describedby', ids.join(' '));
  }

  /** Seeds the shared state once from this view's server-rendered markup. */
  #seed() {
    const checked = this.querySelector('.cautiva-delivery__radio:checked');
    state.metodo = checked instanceof HTMLInputElement ? checked.value : '';
    state.documento = toDigits(this.#docInput?.value ?? '', CUIT_LENGTH);
    state.cuit = toDigits(this.#cuitInput?.value ?? '', CUIT_LENGTH);
    state.facturaWanted = Boolean(this.#facturaToggle?.checked) || state.cuit.length > 0;
    // Recover the persisted CUIT's provenance: one that equals a valid-CUIT document is a
    // live mirror and keeps tracking the document; any other non-empty CUIT was buyer-entered,
    // so pin it (`cuitUserEdited`) and never clobber it on load.
    const mirrorsDocument = state.cuit !== '' && state.cuit === state.documento && isCuitValid(state.documento);
    state.cuitFromDoc = mirrorsDocument;
    state.cuitUserEdited = state.cuit !== '' && !mirrorsDocument;
    normalize();
    seeded = true;
  }

  #onDocInput = () => {
    if (!this.#docInput) return;
    const digits = toDigits(this.#docInput.value, CUIT_LENGTH);
    if (digits !== this.#docInput.value) this.#docInput.value = digits;
    setState({ documento: digits });
  };

  #onCuitInput = () => {
    if (!this.#cuitInput) return;
    const digits = toDigits(this.#cuitInput.value, CUIT_LENGTH);
    if (digits !== this.#cuitInput.value) this.#cuitInput.value = digits;
    // The first keystroke hands the buyer control of the CUIT field: it pins the value and
    // stops the shipping document from mirroring in, even after the buyer clears the field.
    setState({ cuit: digits, cuitFromDoc: false, cuitUserEdited: true });
  };

  /** Reflects shared state into this view's controls and validation display. */
  render() {
    for (const radio of this.#radios ?? []) radio.checked = radio.value === state.metodo;

    // Clear the "choose a method" error once a choice exists (the gate reveals it).
    if (this.#methodError && methodChosen()) this.#methodError.hidden = true;

    if (this.#facturaToggle) this.#facturaToggle.checked = state.facturaWanted;

    if (this.#docInput && this.#docInput.value !== state.documento) {
      this.#docInput.value = state.documento;
    }
    if (this.#cuitInput && this.#cuitInput.value !== state.cuit) {
      this.#cuitInput.value = state.cuit;
    }

    const docInvalid = state.metodo === 'envio' && state.documento.length > 0 && !isDocValid(state.documento);
    this.#setFieldError(this.#docInput, this.#docError, docInvalid);

    const cuitInvalid = state.facturaWanted && state.cuit.length > 0 && !isCuitValid(state.cuit);
    this.#setFieldError(this.#cuitInput, this.#cuitError, cuitInvalid);
  }

  /**
   * @param {HTMLInputElement | null} input
   * @param {HTMLElement | null} error
   * @param {boolean} invalid
   */
  #setFieldError(input, error, invalid) {
    input?.setAttribute('aria-invalid', invalid ? 'true' : 'false');
    if (error) error.hidden = !invalid;
  }

  /** Whether this view is currently rendered (not inside a closed drawer). */
  get visible() {
    return this.offsetParent !== null;
  }

  /**
   * Surfaces the first blocking error; moves focus to it only when `focus` is set (so we
   * focus a single view when several are mounted).
   * @param {boolean} [focus]
   */
  showErrors(focus = true) {
    if (!methodChosen()) {
      if (this.#methodError) this.#methodError.hidden = false;
      if (focus) this.#radios?.[0]?.focus();
      return;
    }
    if (this.#methodError) this.#methodError.hidden = true;

    if (!docSatisfied()) {
      this.#setFieldError(this.#docInput, this.#docError, true);
      if (focus) this.#docInput?.focus();
      return;
    }
    if (!facturaSatisfied()) {
      this.#setFieldError(this.#cuitInput, this.#cuitError, true);
      if (focus) this.#cuitInput?.focus();
    }
  }
}

/** Reveals blocking errors across every mounted view, focusing the first visible one. */
const revealErrors = () => {
  let focusTaken = false;
  for (const view of views) {
    const focus = !focusTaken && view.visible;
    view.showErrors(focus);
    if (focus) focusTaken = true;
  }
};

/**
 * Blocks the cart's checkout submission while the delivery form is incomplete — covers both
 * the `#checkout` button and Enter-to-submit from a text field. Scoped to the checkout
 * submitter so quantity/other `cart-form` actions still work. Accelerated (Shop Pay etc.)
 * buttons are gated separately via CSS `pointer-events` (R2). Capture phase so it beats the
 * cart's own submit handler.
 */
const interceptCheckout = (event) => {
  if (allowed) return;
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form') return;
  // Enter-to-submit reports no submitter; the browser's default submit button is #checkout.
  const submitter = event.submitter;
  if (submitter && submitter.getAttribute('name') !== 'checkout') return;

  event.preventDefault();
  event.stopPropagation();
  revealErrors();
};

/**
 * The cart drawer renders the shared cart-summary with no theme-block hook, so the field is
 * cloned into `.cart-drawer__summary` from the global template. A MutationObserver re-injects
 * whenever the summary is (re)rendered — covering open, synchronous morph and async
 * Section-Rendering paths with no timing race.
 */
class CautivaDeliveryDrawerController {
  /** @type {MutationObserver | null} */
  #observer = null;

  connect() {
    const drawer = document.querySelector('cart-drawer-component');
    if (!drawer) return;
    // Re-inject on every summary re-render, and re-apply the gate — the accelerated buttons
    // re-render on morph and would otherwise lose their blocked state until the next input.
    this.#observer = new MutationObserver(() => {
      this.#inject();
      enforce();
    });
    this.#observer.observe(drawer, { childList: true, subtree: true });
    this.#inject();
    enforce();
  }

  destroy() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #inject() {
    const summary = document.querySelector('.cart-drawer__summary');
    if (!summary || summary.querySelector('cautiva-delivery-capture')) return;

    const template = document.getElementById('cautiva-delivery-template');
    if (!(template instanceof HTMLTemplateElement)) return;

    const ctas = summary.querySelector('.cart__ctas');
    const fragment = template.content.cloneNode(true);
    if (ctas) summary.insertBefore(fragment, ctas);
    else summary.append(fragment);
  }
}

if (!customElements.get('cautiva-delivery-capture')) {
  customElements.define('cautiva-delivery-capture', CautivaDeliveryCapture);
}

document.addEventListener('submit', interceptCheckout, true);

if (!window.cautivaDeliveryDrawerController) {
  const controller = new CautivaDeliveryDrawerController();
  window.cautivaDeliveryDrawerController = controller;
  controller.connect();
}
